import {
  ChangeEvent,
  FunctionComponent,
  SyntheticEvent,
  useContext,
  useState,
} from "react";
import "react-phone-input-2/lib/style.css";

import { AuthService } from "../../services/auth";
import { useLocation, useNavigate } from "react-router-dom";
import PasswordInput from "../PasswordInput";
import { UserContext } from "../../context/UserContext";
import Label from "../Label";
import { useAsync } from "../../utils/hooks/useAsync";
import Button from "../common/Button";
import { useAuth } from "../../utils/hooks/useIsAuth";

type FormData = {
  email: string;
  password: string;
};

enum LoginError {
  defaultError,
  notAuthorized,
  notConfirmed,
}

const AuthForm: FunctionComponent = () => {
  const [formData, setFormData] = useState<FormData>({
    email: "",
    password: "",
  });

  const [logInError, setLogInError] = useState<LoginError | null>(null);
  const { defaultError, notAuthorized, notConfirmed } = LoginError;
  const LogInErrorMessage = (logInError: LoginError) => {
    switch (logInError) {
      case defaultError:
        return "Error del servidor. Intente nuevamente más tarde";
      case notAuthorized:
        return "Por favor, verifique su nombre de usuario y contraseña e intente nuevamente.";
      case notConfirmed:
        return "Esta cuenta aún no fue confirmada.";
      default:
        return "Error del servidor. Intente nuevamente más tarde";
    }
  };

  const { setUser, user } = useContext(UserContext);
  const { saveAuthCookies } = useAuth();

  const { email, password } = formData;
  const { isLoading, run } = useAsync();
  const navigate = useNavigate();
  const location = useLocation();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLoginError = (err: any) => {
    if (err?.data?.message?.name === "UserNotConfirmedException") {
      setLogInError(notConfirmed);
    } else if (err?.data?.message?.name === "NotAuthorizedException") {
      setLogInError(notAuthorized);
    } else {
      setLogInError(defaultError);
    }
  };

  const handleLogin = (e: SyntheticEvent) => {
    e.preventDefault();
    run(
      AuthService.login({
        email,
        password,
      })
    )
      .then((data: any) => {
        if (data.tokenData && data.userData) {
          setUser({
            ...user,
            email: data.userData?.email,
            name: data.userData?.name,
            lastName: data.userData?.lastName ?? "",
            phoneNumber: data.userData?.phoneNumber,
            companyName: data.userData?.companyName ?? "",
          });

          saveAuthCookies({ ...data.tokenData, email });

          const from = location.state?.from;
          if (from && typeof from === "string" && from.startsWith("/")) {
            navigate(from);
          } else {
            navigate("/");
          }
        }

        handleLoginError(data);
        return false;
      })
      .catch((err) => {
        handleLoginError(err);
      });
  };

  return (
    <section className="auth_form_container">
      <form onSubmit={handleLogin} className="auth_form">
        <label htmlFor="email">Email</label>
        <input name="email" type="text" id="email" onChange={handleChange} />
        <div className="auth_form__password-container">
          <label htmlFor="password">Contraseña*</label>
          <a
            className="auth_form__recovery-password"
            onClick={() => navigate("/forgot-password")}
          >
            Olvidé mi contraseña
          </a>
        </div>
        <PasswordInput handleChange={handleChange} validPassword={true} />
        <Button
          isLoading={isLoading}
          className="auth_form__button"
          type="submit"
          id="loginbutton"
        >
          Ingresar
        </Button>
        {logInError !== null ? (
          <Label tag="p" variants="body2" className="login__error">
            {LogInErrorMessage(logInError)}
            {logInError === notConfirmed && (
              <span
                onClick={() => {
                  AuthService.resendLink(user.email);
                }}
              >
                {"Reenviar email de confirmación"}
              </span>
            )}
          </Label>
        ) : null}
      </form>
    </section>
  );
};

export default AuthForm;
